import React, { FC } from 'react';

import { Button, Typography } from '@mui/material';
import { Box, SxProps } from '@mui/system';

import { ReactComponent as MenuIcon } from '@pharmaplan/common/assets/icons/menuIcon.svg';
import { PharmacyLegendVariant } from '@pharmaplan/common';

import { ChipType, SkeletonVariants } from '../../../helpers/Constants';
import OptimizedAvatar from '../OptimizedAvatar/OptimizedAvatar';
import withSkeleton from '../WithSkeleton/WithSkeleton';
import PostedByAgencyTag from '../PostedByAgencyTag';
import CustomMenuList from '../CustomMenuList';
import CustomChip from '../CustomChip';

import styles from './styles';
import genericClasses from '../../../theme/GenericClasses';
import strings from '../../../localization';

export interface IMenuActions {
  key: string;
  onClick: () => void;
  label: string | string[];
}

type GenericStyle = Record<string, unknown>;

interface ICustomAvatarSection {
  config: Array<{ key: string; value: string | React.ReactNode }>;
  title: string;
  menuActions: Array<IMenuActions> | null;
  successAction: string;
  avatar?: string | null;
  hourlyRate?: number;
  configStyle?: GenericStyle;
  customFooter?: React.ReactNode | boolean;
  customContainerStyle?: GenericStyle;
  customMenuContainerStyle?: GenericStyle;
  customDetailContainerStyle?: GenericStyle;
  customTitleStyle?: GenericStyle;
  avatarSize?: number;
  imageSuccessAction?: string;
  favorite?: boolean;
  showFullImage?: boolean;
  onClick?: (e: any) => void;
  isAutoSyncRecord?: boolean;
  shiftType?: PharmacyLegendVariant;
  RightSection?: React.ReactNode;
  TopSection?: React.ReactNode;
  renderChip?: React.ReactNode | null;
}

const CustomAvatarSection: FC<ICustomAvatarSection> = ({
  config,
  title,
  avatar,
  hourlyRate,
  menuActions,
  successAction,
  customContainerStyle,
  customDetailContainerStyle,
  configStyle,
  customMenuContainerStyle,
  customTitleStyle,
  customFooter,
  imageSuccessAction,
  favorite,
  avatarSize = 60,
  showFullImage = true,
  onClick,
  isAutoSyncRecord,
  shiftType,
  RightSection,
  TopSection,
  renderChip,
}) => {
  const SkeletonTypography = withSkeleton({
    successAction,
    variant: SkeletonVariants.text,
  })(Typography);

  const style = styles(avatarSize);

  return (
    <Box sx={[style.root, customContainerStyle ?? {}]}>
      {(isAutoSyncRecord || TopSection) && (
        <Box sx={genericClasses.flex}>
          {TopSection}
          {isAutoSyncRecord && <PostedByAgencyTag type={shiftType} />}
        </Box>
      )}
      <Box sx={style.container}>
        <Box
          component={onClick ? Button : 'span'}
          onClick={onClick}
          sx={[
            style.container,
            style.subContainer,
            onClick ? style.hoverlessButton : {},
          ]}
        >
          <OptimizedAvatar
            successAction={imageSuccessAction}
            avatarSize={avatarSize}
            src={avatar}
            favorite={favorite}
            showFullImage={showFullImage}
          />

          <Box
            sx={[
              customDetailContainerStyle ?? style.detailsContainer,
              style.endContainer,
            ]}
          >
            <SkeletonTypography sx={customTitleStyle ?? style.title}>
              {title}
            </SkeletonTypography>
            {config.map((item) =>
              (
                <SkeletonTypography
                  key={item.key}
                  sx={[style.subtitles, configStyle] as SxProps}
                >
                  {item.value}
                </SkeletonTypography>
              ))}
          </Box>
        </Box>
        <Box sx={style.endContainer}>
          {RightSection ?? (
            <>
              {menuActions && (
                <Box sx={customMenuContainerStyle ?? style.menuContainer}>
                  <CustomMenuList
                    margin={10}
                    disableFixedDropdown
                    customMenuTextStyle={style.menuText}
                    customIconButtonStyle={style.button}
                    items={menuActions}
                  >
                    <MenuIcon height={24} width={24} />
                  </CustomMenuList>
                </Box>
              )}
              <Box sx={style.strikeContainer}>
                {renderChip
                  ?? (!!hourlyRate && (
                    <CustomChip
                      label={`$${hourlyRate}${strings.perHour}`}
                      type={ChipType.hourlyRate}
                    />
                  ))}
              </Box>
            </>
          )}
        </Box>
      </Box>
      {!!customFooter && <Box sx={style.footerDivider}>{customFooter}</Box>}
    </Box>
  );
};
export default CustomAvatarSection;
